import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import 'pdfjs-dist/legacy/build/pdf.worker.entry';
import { downloadTncReviewPdfData, getPreloadedTncPdfData } from 'utils/tncPdf';

const MAX_RENDER_SCALE = 2.7;
const MIN_CANVAS_OUTPUT_SCALE = 3;
const MAX_DEVICE_PIXEL_RATIO = 3;
const PAGE_HORIZONTAL_PADDING = 24;
const PDF_ZOOM_FACTOR = 1;
const LAZY_RENDER_OFFSET = 1200;

export const getPdfRenderScale = (containerWidth, viewportWidth) => {
  if (!containerWidth || !viewportWidth) {
    return 1;
  }

  const availableWidth = Math.max(containerWidth - PAGE_HORIZONTAL_PADDING, 1);
  const widthScale = (availableWidth / viewportWidth) * PDF_ZOOM_FACTOR;

  return Math.min(MAX_RENDER_SCALE, widthScale);
};

export const getCanvasOutputScale = () => {
  if (globalThis.window === undefined) {
    return MIN_CANVAS_OUTPUT_SCALE;
  }

  return Math.min(Math.max(globalThis.window.devicePixelRatio || 1, MIN_CANVAS_OUTPUT_SCALE), MAX_DEVICE_PIXEL_RATIO);
};

export const loadTncPdfDocument = async (pdfUrl) => {
  const pdfData = await getPreloadedTncPdfData(pdfUrl) || await downloadTncReviewPdfData(pdfUrl);

  if (!pdfData) {
    throw new Error('Terms and conditions PDF data is empty');
  }

  return pdfjsLib.getDocument({ data: pdfData }).promise;
};

export const getPageNumberFromElement = (pageElement) => Number(pageElement.dataset.pageNumber);

export const isPageWithinLazyRenderRange = (pageRect, scrollRect) => (
  pageRect.top <= scrollRect.bottom + LAZY_RENDER_OFFSET
  && pageRect.bottom >= scrollRect.top - LAZY_RENDER_OFFSET
);

export const getPdfPagePlaceholderSize = (viewport) => ({
  width: `${Math.floor(viewport.width)}px`,
  height: `${Math.floor(viewport.height)}px`,
});

export const createPdfPageElement = (pageNumber, totalPages, placeholderViewport) => {
  const pageElement = document.createElement('div');
  const pageLabel = document.createElement('div');
  const placeholder = document.createElement('div');
  const placeholderSize = getPdfPagePlaceholderSize(placeholderViewport);

  pageElement.className = 'tnc-pdfjs-page';
  pageElement.dataset.pageNumber = `${pageNumber}`;
  pageLabel.className = 'tnc-pdfjs-page-label';
  pageLabel.textContent = `${pageNumber} / ${totalPages}`;
  placeholder.className = 'tnc-pdfjs-page-placeholder';
  placeholder.style.width = placeholderSize.width;
  placeholder.style.height = placeholderSize.height;
  placeholder.textContent = 'Loading page...';

  pageElement.appendChild(pageLabel);
  pageElement.appendChild(placeholder);

  return pageElement;
};

export const configurePdfCanvas = (canvas, viewport, outputScale, pageNumber, totalPages) => {
  canvas.className = 'tnc-pdfjs-canvas';
  canvas.width = Math.floor(viewport.width * outputScale);
  canvas.height = Math.floor(viewport.height * outputScale);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;
  canvas.setAttribute('aria-label', `${pageNumber} / ${totalPages}`);

  return canvas;
};

export const getPdfRenderTransform = (outputScale) => (
  outputScale === 1 ? null : [outputScale, 0, 0, outputScale, 0, 0]
);

export const getScrollRenderRect = (scrollElement) => scrollElement?.getBoundingClientRect?.() || {
  top: 0,
  bottom: globalThis.window.innerHeight || globalThis.document.documentElement.clientHeight,
};

export const shouldSkipPageRender = (pageNumber, renderedPageNumbers, renderingPageNumbers) => (
  !pageNumber || renderedPageNumbers.has(pageNumber) || renderingPageNumbers.has(pageNumber)
);

export const resetTncPdfContent = (contentElement, lastPageRef) => {
  while (contentElement.firstChild) {
    contentElement.firstChild.remove();
  }

  if (lastPageRef) {
    lastPageRef.current = null;
  }
};

export const appendPdfPagePlaceholders = ({
  contentElement,
  totalPages,
  placeholderViewport,
  lastPageRef,
  isCancelled,
}) => {
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    if (isCancelled()) {
      return false;
    }

    const pageElement = createPdfPageElement(pageNumber, totalPages, placeholderViewport);
    contentElement.appendChild(pageElement);

    if (pageNumber === totalPages && lastPageRef) {
      lastPageRef.current = pageElement;
    }
  }

  return true;
};

export const notifyPdfRenderComplete = (onRenderCompleteRef) => {
  if (onRenderCompleteRef.current) {
    onRenderCompleteRef.current();
  }
};

export const registerPdfRenderListeners = (contentElement, queueRenderVisiblePages) => {
  const scrollElement = contentElement.closest?.('.tnc-review-window') || contentElement.parentElement;

  scrollElement?.addEventListener?.('scroll', queueRenderVisiblePages, { passive: true });
  globalThis.window.addEventListener('resize', queueRenderVisiblePages);

  return scrollElement;
};

export const reportTncPdfLoadError = (error) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('Failed to load terms and conditions PDF:', error);
  }
};

export const reportTncPdfPageRenderError = (error, isCancelled) => {
  if (!isCancelled() && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('Failed to render terms and conditions PDF page:', error);
  }
};

export const renderTncPdfPage = async ({
  contentElement,
  firstPage,
  isCancelled,
  loadedPdf,
  onRenderCompleteRef,
  pageNumber,
  renderedPageNumbers,
  renderingPageNumbers,
}) => {
  if (isCancelled() || shouldSkipPageRender(pageNumber, renderedPageNumbers, renderingPageNumbers)) {
    return;
  }

  renderingPageNumbers.add(pageNumber);

  const pageElement = contentElement.querySelector(`[data-page-number="${pageNumber}"]`);

  if (!pageElement) {
    renderingPageNumbers.delete(pageNumber);
    return;
  }

  try {
    const page = pageNumber === 1 ? firstPage : await loadedPdf.getPage(pageNumber);
    const pageViewport = page.getViewport({ scale: 1 });
    const scale = getPdfRenderScale(contentElement.clientWidth, pageViewport.width);
    const viewport = page.getViewport({ scale });
    const outputScale = getCanvasOutputScale();
    const placeholder = pageElement.querySelector('.tnc-pdfjs-page-placeholder');
    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');

    if (!canvasContext) {
      throw new Error('Canvas 2D context is not available');
    }

    if (placeholder) {
      placeholder.remove();
    }

    configurePdfCanvas(canvas, viewport, outputScale, pageNumber, loadedPdf.numPages);
    pageElement.appendChild(canvas);

    await page.render({
      canvasContext,
      viewport,
      transform: getPdfRenderTransform(outputScale),
    }).promise;

    renderedPageNumbers.add(pageNumber);
    pageElement.classList.add('tnc-pdfjs-page-rendered');
    notifyPdfRenderComplete(onRenderCompleteRef);
  } catch (error) {
    reportTncPdfPageRenderError(error, isCancelled);
  } finally {
    renderingPageNumbers.delete(pageNumber);
  }
};

export const renderVisibleTncPdfPages = ({
  contentElement,
  isCancelled,
  renderedPageNumbers,
  renderingPageNumbers,
  renderPage,
  scrollElement,
}) => {
  if (isCancelled()) {
    return;
  }

  const scrollRect = getScrollRenderRect(scrollElement);
  const pageElements = contentElement.querySelectorAll('.tnc-pdfjs-page');

  pageElements.forEach((pageElement) => {
    const pageNumber = getPageNumberFromElement(pageElement);

    if (shouldSkipPageRender(pageNumber, renderedPageNumbers, renderingPageNumbers)) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();

    if (isPageWithinLazyRenderRange(pageRect, scrollRect)) {
      renderPage(pageNumber);
    }
  });
};

export const queueLazyRenderVisiblePages = (lazyRenderTimer, setLazyRenderTimer, renderVisiblePages) => {
  if (lazyRenderTimer) {
    return;
  }

  const nextLazyRenderTimer = globalThis.setTimeout(() => {
    setLazyRenderTimer(null);
    renderVisiblePages();
  }, 50);

  setLazyRenderTimer(nextLazyRenderTimer);
};

const TncPdfViewer = ({
  pdfUrl,
  lastPageRef,
  onRenderComplete,
  disableRendering = process.env.NODE_ENV === 'test',
}) => {
  const contentRef = useRef(null);
  const onRenderCompleteRef = useRef(onRenderComplete);
  const [viewportWidth, setViewportWidth] = useState(() => (
    globalThis.window === undefined ? 0 : globalThis.window.innerWidth
  ));
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onRenderCompleteRef.current = onRenderComplete;
  }, [onRenderComplete]);

  useEffect(() => {
    if (disableRendering) {
      return undefined;
    }

    let resizeTimer = null;

    const handleWindowResize = () => {
      if (resizeTimer) {
        globalThis.clearTimeout(resizeTimer);
      }

      resizeTimer = globalThis.setTimeout(() => {
        resizeTimer = null;
        setViewportWidth(globalThis.window.innerWidth || 0);
      }, 150);
    };

    globalThis.window.addEventListener('resize', handleWindowResize);

    return () => {
      if (resizeTimer) {
        globalThis.clearTimeout(resizeTimer);
      }

      globalThis.window.removeEventListener('resize', handleWindowResize);
    };
  }, [disableRendering]);

  useEffect(() => {
    if (disableRendering) {
      return undefined;
    }

    let isCancelled = false;
    let loadedPdf = null;
    let scrollElement = null;
    let lazyRenderTimer = null;
    let queueRenderVisiblePagesHandler = null;
    const contentElement = contentRef.current;

    const setLazyRenderTimer = (nextLazyRenderTimer) => {
      lazyRenderTimer = nextLazyRenderTimer;
    };

    const clearLazyRenderTimer = () => {
      if (lazyRenderTimer) {
        globalThis.clearTimeout(lazyRenderTimer);
        lazyRenderTimer = null;
      }
    };

    const renderPdf = async () => {
      setIsLoading(true);
      setHasError(false);

      if (!contentElement) {
        return;
      }

      resetTncPdfContent(contentElement, lastPageRef);

      try {
        loadedPdf = await loadTncPdfDocument(pdfUrl);

        if (isCancelled) {
          return;
        }

        const firstPage = await loadedPdf.getPage(1);
        const firstPageViewport = firstPage.getViewport({ scale: 1 });
        const initialScale = getPdfRenderScale(contentElement.clientWidth, firstPageViewport.width);
        const placeholderViewport = firstPage.getViewport({ scale: initialScale });
        const renderedPageNumbers = new Set();
        const renderingPageNumbers = new Set();
        const isRenderCancelled = () => isCancelled;

        const renderPage = (pageNumber) => renderTncPdfPage({
          contentElement,
          firstPage,
          isCancelled: isRenderCancelled,
          loadedPdf,
          onRenderCompleteRef,
          pageNumber,
          renderedPageNumbers,
          renderingPageNumbers,
        });

        const renderVisiblePages = () => {
          renderVisibleTncPdfPages({
            contentElement,
            isCancelled: isRenderCancelled,
            renderedPageNumbers,
            renderingPageNumbers,
            renderPage,
            scrollElement,
          });
        };

        const queueRenderVisiblePages = () => {
          queueLazyRenderVisiblePages(lazyRenderTimer, setLazyRenderTimer, renderVisiblePages);
        };

        const pagesCreated = appendPdfPagePlaceholders({
          contentElement,
          totalPages: loadedPdf.numPages,
          placeholderViewport,
          lastPageRef,
          isCancelled: isRenderCancelled,
        });

        if (!pagesCreated) {
          return;
        }

        if (!isCancelled) {
          queueRenderVisiblePagesHandler = queueRenderVisiblePages;
          scrollElement = registerPdfRenderListeners(contentElement, queueRenderVisiblePagesHandler);
          renderVisiblePages();

          setIsLoading(false);
          notifyPdfRenderComplete(onRenderCompleteRef);
        }
      } catch (error) {
        if (!isCancelled) {
          reportTncPdfLoadError(error);

          setIsLoading(false);
          setHasError(true);
        }
      }
    };

    renderPdf();

    return () => {
      isCancelled = true;
      clearLazyRenderTimer();

      if (queueRenderVisiblePagesHandler) {
        scrollElement?.removeEventListener?.('scroll', queueRenderVisiblePagesHandler);
        globalThis.window.removeEventListener('resize', queueRenderVisiblePagesHandler);
      }

      if (loadedPdf) {
        loadedPdf.destroy();
      }
    };
  }, [disableRendering, pdfUrl, lastPageRef, viewportWidth]);

  return (
    <div className='tnc-pdfjs-viewer'>
      {isLoading && (
        <div className='tnc-pdfjs-status'>Loading terms and conditions...</div>
      )}
      {hasError && (
        <div className='tnc-pdfjs-status tnc-pdfjs-status-error'>Unable to load terms and conditions.</div>
      )}
      <div ref={contentRef} className='tnc-pdfjs-content' />
    </div>
  );
};

export default TncPdfViewer;
